import { FormlyFieldConfig } from '@ngx-formly/core';
import { format } from 'date-fns';
import { flatten } from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, map, shareReplay, take, takeUntil, tap } from 'rxjs/operators';

import {
  Renderer2,
  ElementRef,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
  InjectionToken,
  Inject,
  AfterViewInit,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { MatTableDataSource } from '@angular/material/table';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';

import { MxEventName, MxPropertyName } from '@axon-enterprise/track';
import { SnSearchAPI } from '@lib/api/services';
import { SnCurrentOrganization } from '@lib/auth';
import { INPUT_TYPE } from '@lib/common/constants/formly-constants';
import { FsFormatDatePipe } from '@lib/common/pipes';
import { FsTimeService } from '@lib/common/services';
import { FsDataDogRumService } from '@lib/data-dog-rum';
import { SnFormFieldConfig } from '@lib/form';
import { Modes, Styles } from '@lib/form/date-range-picker';
import { MixpanelService } from '@lib/mixpanel';
import { LocaleService, t } from '@lib/translation';
import { SnLayoutService } from '@lib/ui/layout';

import {
  ERROR_TO_FILTER_NAME,
  FsLprAxonVIFilterSuggestion,
  FsLprAxonVIUser,
  FsLprFleetRead,
  IFsLprParameters,
  IFsLprProviderInterface,
  IFsLprResult,
  LPR_ERRORS,
  LprWarningEntry,
  PlateResultSort,
  RecordType,
  FleetRecordType,
  LprExportServiceAPI,
  FsLprAxonVIRead,
  ILprExportParamsInterface,
} from '../../api';
import { IHitsInterface } from '../../api/interfaces/lpr-query-result.interface';
import {
  FlockProviderService,
  FususProviderService,
  VigilantProviderService,
  AxonVIProviderService,
  FlockV3ProviderService,
  CAMERA_MODELS,
} from '../../api/providers';
import { AxonVILprService } from '../../integrations';
import { AlprExportDialogData, AlprCsvExportConfiguration } from '../../interfaces';
import {
  FIELD_APPEARANCE_OUTLINE,
  MIN_REASON_LENGTH,
  US_STATES,
  VEHICLE_BODY_TYPE,
  VEHICLE_COLOR,
  VEHICLE_MAKE,
} from '../../interfaces/alpr-reads-search.interface';
import {
  PlateReviewService,
  PlatesService,
  PlateImageCropService,
  LprExportRequestFormatterService,
  LPRSourceManagerService,
} from '../../services';
import { ViLPRProvider } from '../../shared';
import { CsvExportDialogComponent } from '../csv-export-dialog/csv-export-dialog.component';
import { LprViewStagesComponent } from '../lpr-view-stages/lpr-view-stages.component';
import { PlateMapComponent } from '../plate-map/plate-map.component';
import { LPRProviderSelection, AxonViLPRSearchEntity } from './source-management/models';

// Base configurations
const baseFieldConfig = {
  appearance: FIELD_APPEARANCE_OUTLINE,
  floatLabel: 'always',
  emptyOption: true,
  emptyLabel: 'Any',
};

export const OUTPOST_PROVIDER = new InjectionToken<AxonVIProviderService>('OutpostProvider');
export const FLEET_NEW_ENDPOINT_PROVIDER = new InjectionToken<AxonVIProviderService>(
  'FleetNewEndpointProvider',
);
export const LIGHTPOST_PROVIDER = new InjectionToken<AxonVIProviderService>('LightpostProvider');
export const SHARED_RECORDS_PROVIDER = new InjectionToken<AxonVIProviderService>('SharedRecordsProvider');

export const SHOW_MAP = 'alpr.mapButtons.show';
export const HIDE_MAP = 'alpr.mapButtons.hide';

// Custom form model type that allows boolean for recordType toggle
interface IAlprSearchFormModel extends Omit<Partial<IFsLprParameters>, 'recordType'> {
  hitsOnly?: boolean; // Toggle: true = HIT, false = ALL
  additionalDetails?: string[];
}

@Component({
  selector: 'fs-alpr-reads-search',
  templateUrl: './alpr-reads-search.component.html',
  styleUrls: ['./alpr-reads-search.component.scss'],

  providers: [
    {
      provide: OUTPOST_PROVIDER,
      useFactory: (axonVIService: AxonVILprService, currentOrg: SnCurrentOrganization) =>
        new AxonVIProviderService(axonVIService, 'Outpost', currentOrg),
      deps: [AxonVILprService, SnCurrentOrganization],
    },
    {
      provide: LIGHTPOST_PROVIDER,
      useFactory: (axonVIService: AxonVILprService, currentOrg: SnCurrentOrganization) =>
        new AxonVIProviderService(axonVIService, 'Lightpost', currentOrg),
      deps: [AxonVILprService, SnCurrentOrganization],
    },
    {
      provide: FLEET_NEW_ENDPOINT_PROVIDER,
      useFactory: (axonVIService: AxonVILprService, currentOrg: SnCurrentOrganization) =>
        new AxonVIProviderService(axonVIService, 'Fleet 3', currentOrg),
      deps: [AxonVILprService, SnCurrentOrganization],
    },
    {
      provide: SHARED_RECORDS_PROVIDER,
      useFactory: (axonVIService: AxonVILprService, currentOrg: SnCurrentOrganization) =>
        new AxonVIProviderService(axonVIService, 'Shared', currentOrg),
      deps: [AxonVILprService],
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AlprReadsSearchComponent implements OnInit, AfterViewInit, OnDestroy {
  private static readonly DRAWER_CONFIG = {
    cardMinWidth: 375, // Min width of a single card from CSS
    cardGap: 8, // 8px gap from CSS
    containerPadding: 16, // Left/right padding of container
    minWidth: 400,
  };

  @ViewChild(LprViewStagesComponent)
  public lprViewStagesComponent: LprViewStagesComponent;
  @ViewChild('resultsWrapper', { static: false }) resultsWrapperRef: ElementRef;
  @ViewChild('contentArea', { static: false }) contentAreaRef: ElementRef;

  @ViewChild(PlateMapComponent)
  public plateMapComponent: PlateMapComponent;

  public isResizing = false;
  public startX: number;
  public startWidth: number;

  public isDrawerOpen = false;
  public readonly form = new UntypedFormGroup({ plate: new UntypedFormControl() });
  public formModel: IAlprSearchFormModel = {};
  // Array of SnFormFieldConfig which is used to create fields for FormlyForm
  public formFields: SnFormFieldConfig[] = [];
  // Variable to check if date picker has been initialised or not
  private _datepickerInitialized: boolean;
  // Search results
  public results: IFsLprResult[] = [];
  public historicalResults: IFsLprResult[] = [];
  public pendingGeocodeRequests: number = 0;
  public pageSize: number;

  // Flag to control auto-zoom behavior - only true when "Search" button is clicked
  private _shouldAutoZoom: boolean = false;

  // Prevents source selection UI changes from canceling in-flight deep link requests
  public isSdmDeepLinkActive = false;

  public sort: PlateResultSort = PlateResultSort.DESC;

  public totalResultsCount: {
    total: number;
    totalQueryResults: number;
  } = {
    total: 0,
    totalQueryResults: 0,
  };

  // Variable for total days
  public totalDays: number;

  public isLoading: boolean = false;
  public hasLoaded: boolean = false;
  public hasSearched = false;

  // Export button enabled state
  public exportButtonEnabled: boolean = false;

  public hasBoundingBoxParams: boolean = false;
  public resultsStyle: Record<string, string> = {};

  public draggedWidth: number;
  public lightboxImages: string[] = [];
  public lightboxVisible: boolean = false;
  public providers: IFsLprProviderInterface[] = [];
  public activeProvider: IFsLprProviderInterface | null = null;

  // #region Source & Device Management
  public activeProviders: IFsLprProviderInterface[] = []; // Providers currently shown to user
  public cameraVendors: string[] = []; // Selected Axon camera vendors for filtering
  public cameraModels: string[] = []; // Selected Axon camera models for filtering
  public shouldShowPartialMatches = false; // Whether to show results with warnings
  // #endregion Source & Device Management

  public pageLimit: number = 50;
  public page = 1;
  public allFinished = false;
  public startTime = new Date();
  public mode: string = 'cards';
  public mapButtonText: string = SHOW_MAP;
  public isReviewingPlates: boolean = false;
  public reviewResults: IFsLprResult[] = [];
  public errors: string[] = [];
  public warnings: LprWarningEntry[] = [];
  public isLoadingHistoricalResults: boolean = false;
  public hideDetails: boolean = false;
  public readonly MAP_TAB_LABEL = 'alpr.mapTabLabel';
  public readonly READ_LIST_TAB_LABEL = 'alpr.readListTabLabel';
  public readonly HITS_TAB_LABEL = 'alpr.hitsTabLabel';
  public defaultHistoricalSearchTimeOption = 7;
  public historicalSearchTimeOptions = [
    { label: 'alpr.historicalSearchTimeOptions.last7Days', value: 7 },
    { label: 'alpr.historicalSearchTimeOptions.last14Days', value: 14 },
    { label: 'alpr.historicalSearchTimeOptions.last30Days', value: 30 },
  ];

  public readonly HIT_TYPE = FleetRecordType.HIT;

  // The currently selected days for historical search
  private _selectedHistoricalDays = 7;

  public get vehicleIntelligenceEnabled(): boolean {
    return this._currentOrg.enabledIntegrations.includes('axon_vi');
  }

  public get fususEnabled(): boolean {
    return true;
  }

  public get vigilantEnabled(): boolean {
    return this._currentOrg.enabledIntegrations.includes('vigilant_ps');
  }

  public get flockEnabled(): boolean {
    return this._currentOrg.enabledIntegrations.includes('flock_ps');
  }

  public get flockV3Enabled(): boolean {
    return this._currentOrg.enabledIntegrations.includes('flock_ps_v3');
  }

  public get lprCsvExportEnabled(): boolean {
    return this._currentOrg.settings?.lprSearchExportEnabled ?? false;
  }

  public get lprSharingEnabled(): boolean {
    return this._currentOrg.settings?.lprSharingManagementEnabled ?? false;
  }

  public get sourceAndDeviceManagementEnabled(): boolean {
    return this._currentOrg.settings?.sourceAndDeviceManagementEnabled ?? false;
  }

  public get shouldShowHeaderPlaceholder(): boolean {
    if (this.sourceAndDeviceManagementEnabled) {
      return !this.hasSearched && this.results.length === 0;
    }
    return !this.hasLoaded && this.results.length === 0;
  }

  public get shouldShowSdmSourceSelect(): boolean {
    return !this.isReviewingPlates && this.sourceAndDeviceManagementEnabled;
  }

  public get shouldShowProviderButtons(): boolean {
    return !this.isReviewingPlates && !this.sourceAndDeviceManagementEnabled;
  }

  public queryListStyle: object = {};
  public mapStyle: object = {};
  public iconStyle: object = {};
  public thumbnailImages: { croppedImage: string; plateId: string }[] = [];
  public fields: any;
  public isFullscreen: boolean = false;
  public didQuerySearch: boolean = false;

  // Add this property to track if we're in selected plate (historical view mode
  public isHistoricalView: boolean = false;

  /** Plate selected */
  public selectedPlate: IFsLprResult | null = null;

  private _destroy$ = new Subject<void>();
  private readonly _historicalRequestsCancel$ = new Subject<void>();
  private _providerLoadedSubscriptions = new Map<IFsLprProviderInterface, Subscription>();

  public displayedColumns: string[] = ['readTime', 'thumbnail', 'location', 'deviceName'];
  public tableResults: MatTableDataSource<any>;

  public closedPlateDetails$ = new Subject<IFsLprResult>();

  private _resizeRAF: number | null = null;

  private _mouseMoveListener: () => void;
  private _mouseUpListener: () => void;
  private _touchMoveListener: () => void;
  private _touchEndListener: () => void;
  private _currentCalculatedWidth: number = 0;

  constructor(
    private readonly _cd: ChangeDetectorRef,
    private readonly _currentOrg: SnCurrentOrganization,
    private readonly _toastrService: ToastrService,
    private readonly _imageCropService: PlateImageCropService,
    private readonly _platesService: PlatesService,
    private readonly _mixpanelService: MixpanelService,
    private readonly _timeService: FsTimeService,
    @Inject(OUTPOST_PROVIDER) private readonly _outpostLprProviderService: AxonVIProviderService,
    @Inject(LIGHTPOST_PROVIDER)
    private readonly _lightpostLprProviderService: AxonVIProviderService,
    @Inject(FLEET_NEW_ENDPOINT_PROVIDER)
    private readonly _fleetNewEndpointProviderService: AxonVIProviderService,
    @Inject(SHARED_RECORDS_PROVIDER)
    private readonly _sharedRecordsProviderService: AxonVIProviderService,
    private readonly _fususLprProviderService: FususProviderService,
    private readonly _flockLprProviderService: FlockProviderService,
    private readonly _flockV3LprProviderService: FlockV3ProviderService,
    private readonly _vigilantLprService: VigilantProviderService,
    private readonly _plateReviewService: PlateReviewService,
    private readonly _layoutService: SnLayoutService,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _dataDogRumService: FsDataDogRumService,
    private readonly _searchAPI: SnSearchAPI,
    private readonly _router: Router,
    private readonly _renderer: Renderer2,
    private readonly _axonVILprService: AxonVILprService,
    private readonly _lprExportService: LprExportServiceAPI,
    private readonly _matDialog: MatDialog,
    private readonly _localeService: LocaleService,
    private readonly _requestFormatter: LprExportRequestFormatterService,
    private readonly _lprSourceManagerService: LPRSourceManagerService,
  ) {
    this.resultsStyle = {};
  }

  public ngOnInit(): void {
    // Add global event listeners
    this._addGlobalEventListeners();

    this._genFormFields();

    if (this._platesService.shapeComplete$) {
      this._platesService.shapeComplete$.pipe(takeUntil(this._destroy$)).subscribe(() => {
        // Only search if this is a map view search (not shape drawing)
        if (
          Object.keys(this._platesService.queriesSearchParams).length > 0 &&
          this.form.valid &&
          this._platesService.hasMapViewSearched
        ) {
          this.search();
        }

        // Ensure map markers remain visible after drawing is completed
        if (this.plateMapComponent && !this._platesService.isDragging) {
          // Update markers immediately if we're not in a dragging operation
          this.plateMapComponent['_updateMarkers']();
          this._cd.markForCheck();
        }
      });
    }

    // SDM: Initialize all providers for parallel searching and source selection
    if (this.sourceAndDeviceManagementEnabled) {
      this._initializeProviders();
      this._setupProviderLoadedSubscription();
    } else {
      // Non-SDM: Initialize single provider at a time
      if (this.vehicleIntelligenceEnabled) {
        this.providers.push(this._outpostLprProviderService);
        this.providers.push(this._lightpostLprProviderService);
        this.providers.push(this._fleetNewEndpointProviderService);
        this._outpostLprProviderService.init();
        this._lightpostLprProviderService.init();
        this._fleetNewEndpointProviderService.init();

        if (this.lprSharingEnabled) {
          this.providers.push(this._sharedRecordsProviderService);
          this._sharedRecordsProviderService.init();
        }
      }

      if (this.fususEnabled) {
        this.providers.push(this._fususLprProviderService);
        this._fususLprProviderService.init();
      }

      if (this.vigilantEnabled) {
        this.providers.push(this._vigilantLprService);
        this._vigilantLprService.init();
      }

      // Prefer original Flock provider over Flock V3
      if (this.flockEnabled) {
        this.providers.push(this._flockLprProviderService);
        this._flockLprProviderService.init();
      } else if (this.flockV3Enabled) {
        // Only use Flock V3 if original Flock is not available
        this.providers.push(this._flockV3LprProviderService);
        this._flockV3LprProviderService.init();
      }
    }

    this._initPlateReview();

    this._checkForPlateParam();
    this._checkBoundingBoxParams();

    if (this.sourceAndDeviceManagementEnabled) {
      this._checkForSdmDeepLink();
    } else {
      this._checkForReadId();
    }
  }

  public ngAfterViewInit(): void {
    if (this.hasBoundingBoxParams) {
      this.showMap();
    }

    // Set 30-day period for alert-initiated searches after form fields are initialized
    this._setAlertDateRange();
  }

  private _addGlobalEventListeners(): void {
    this._mouseMoveListener = this._renderer.listen('document', 'mousemove', this._onResizeMove);
    this._mouseUpListener = this._renderer.listen('document', 'mouseup', this.onResizeEnd);
    // Add touch event listeners
    this._touchMoveListener = this._renderer.listen('document', 'touchmove', this._onResizeMove);
    this._touchEndListener = this._renderer.listen('document', 'touchend', this.onResizeEnd);
  }

  private _removeGlobalEventListeners(): void {
    if (this._mouseMoveListener) {
      this._mouseMoveListener();
      this._mouseMoveListener = null;
    }

    if (this._mouseUpListener) {
      this._mouseUpListener();
      this._mouseUpListener = null;
    }

    // Clean up touch event listeners
    if (this._touchMoveListener) {
      this._touchMoveListener();
      this._touchMoveListener = null;
    }

    if (this._touchEndListener) {
      this._touchEndListener();
      this._touchEndListener = null;
    }
  }

  public toggleMode(mode: string): void {
    this.mode = mode;
    this._mixpanelService.trackMetric(MxEventName.LprSearchViewAf, {
      [MxPropertyName.ResultViewMode]: mode,
    });
  }

  public providerChange(provider: IFsLprProviderInterface): void {
    this.activeProvider = provider;
    this.page = 1;
    this._updateResults();
    this.lprViewStagesComponent.startAtTop();
    this._updateErrorsFromProviders();
    this._cd.markForCheck();
  }

  public ngOnDestroy(): void {
    // Unsubscribe from all provider subscriptions
    this._providerLoadedSubscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
    this._providerLoadedSubscriptions.clear();

    this._mixpanelService.logSessionDuration(this.startTime, 'ALPR Search');
    this._historicalRequestsCancel$.next();
    this._historicalRequestsCancel$.complete();
    this._destroy$.next();
    this._destroy$.complete();
    this._platesService.queriesSearchParams = {};
    this._removeGlobalEventListeners();
  }

  public backToAllResults(): void {
    this.isReviewingPlates = false;
    this.page = 1;
    this._updateResults();
    this._cd.markForCheck();
  }

  public pageLimitChange(limit: number): void {
    this.pageLimit = limit;
    this.page = 1;
    this._updateResults();
  }

  public firstPage(): void {
    this.page = 1;
    this._updateResults();
    this.lprViewStagesComponent.scrollToTop();
  }

  public nextPage(): void {
    this.page++;
    this._updateResults();
    this.lprViewStagesComponent.scrollToTop();
  }

  public previousPage(): void {
    this.page--;
    this._updateResults();
    this.lprViewStagesComponent.scrollToTop();
  }

  public onHideDetails(hideDetails: boolean): void {
    this.hideDetails = hideDetails;
    this._cd.markForCheck();
  }

  public clearFilters(): void {
    // Preserve the required search reason and date range
    const searchReason = this.formModel.searchReason;
    const fromDate = this.formModel.fromDate;
    const toDate = this.formModel.toDate;

    // Reset the form model
    this.formModel = {
      ...this.formModel,
      searchReason, // Preserve the search reason
      fromDate, // Preserve the from date
      toDate, // Preserve the to date
      plate: null,
      plateState: null,
      vehicleColor: null,
      vehicleMake: null,
      vehicleBody: null,
      hitsOnly: false, // Toggle: false = ALL (default)
      assignedUserIds: null,
      deviceNames: null,
      additionalDetails: null,
      includeSharedData: null,
    };

    // Reset the form to match the model
    this.form.patchValue(this.formModel);

    // Clear camera filters
    if (this.sourceAndDeviceManagementEnabled) {
      this.cameraVendors = [];
      this.cameraModels = [];
      this._removeDeepLink();
    } else {
      this._clearReadIdParams();
    }

    // Don't clear the shape params -- that is done via drawing tools only
  }

  /**
   * Handles map search button click
   */
  public onMapSearchTriggered(): void {
    // Trigger search using current form data and any existing boundary
    this.search();
  }

  /**
   * Sets the auto-zoom flag to enable automatic map bounds fitting after the next search.
   * This is specifically called when the Search button is clicked to ensure results are visible.
   */
  public setAutoZoomFlag(): void {
    // Set flag to enable auto-zoom for the next alpr-reads-search button click search
    this._shouldAutoZoom = true;
  }

  /**
   * Filters results by selected provider. Pre-fetched data allows instant filtering without re-querying.
   * Ignores changes during deep linking to prevent canceling the in-flight request.
   */
  public onProviderSelectionChange(event: LPRProviderSelection): void {
    if (!this.sourceAndDeviceManagementEnabled) {
      return;
    }

    if (this.isSdmDeepLinkActive) {
      return;
    }

    const providerName = event.name;

    if (!event.isSelected) {
      this.page = 1;
      this._deselectProvider(providerName);
      this._updateResults();
      this._cd.markForCheck();
      return;
    }

    // WWA isn't a real provider - it's a UI grouping handled via camera model selection
    if (providerName === ViLPRProvider.WORKS_WITH_AXON) {
      return;
    }

    this.page = 1;
    this._selectProvider(providerName);
    this._updateResults();
    this._cd.markForCheck();
  }

  /**
   * Filters Axon VI results by selected camera vendors/models.
   * Camera selection implicitly activates/deactivates the Axon provider.
   */
  public onCameraModelSelectionChange(event: AxonViLPRSearchEntity[]): void {
    if (!this.sourceAndDeviceManagementEnabled) {
      return;
    }

    if (this.isSdmDeepLinkActive) {
      return;
    }

    this.page = 1;
    this._handleCameraModelSelectionChange(event);
    this._updateResults();
    this._cd.markForCheck();
  }

  /** Toggles visibility of partial match results (results with warnings). */
  public onPartialMatchesDisplayChange(shouldShowPartialMatches: boolean): void {
    if (!this.sourceAndDeviceManagementEnabled) {
      return;
    }

    this.page = 1;
    this.shouldShowPartialMatches = shouldShowPartialMatches;
    this._updateResults();
    this._cd.markForCheck();
  }

  /**
   * Extracts camera vendors/models from selection and manages Axon provider state.
   * No models selected = deactivate Axon provider (hide all Axon results).
   * Any models selected = activate Axon provider (show filtered Axon results).
   */
  private _handleCameraModelSelectionChange(event: AxonViLPRSearchEntity[]): void {
    const selectedVendors = new Set<string>();
    const selectedModels = new Set<string>();

    for (const entity of event) {
      if (!entity || !entity.cameraModels || entity.cameraModels.length === 0) {
        continue;
      }

      // Deep linking can trigger this before facets load, causing undefined vendor
      if (!entity.cameraVendor) {
        continue;
      }

      selectedVendors.add(entity.cameraVendor);

      for (const model of entity.cameraModels) {
        selectedModels.add(model);
      }
    }

    this.cameraVendors = Array.from(selectedVendors);
    this.cameraModels = Array.from(selectedModels);

    const axonProvider = this.providers.find(
      (p) => p.nameEnum === ViLPRProvider.AXON_VI
    );

    if (!this.cameraVendors.length && !this.cameraModels.length) {
      if (axonProvider) {
        this.activeProviders = this.activeProviders.filter(
          (p) => p.nameEnum !== axonProvider.nameEnum,
        );
      }
    } else if (axonProvider) {
      const isAlreadyActive = this.activeProviders.some(
        (p) => p.nameEnum === axonProvider.nameEnum,
      );
      if (!isAlreadyActive) {
        this.activeProviders.push(axonProvider);
      }
    }
  }

  /**
   * This method is used to search for the plate input by the user across all the providers
   * (Flock, Fusus, Vigilant, Fleet).
   */
  public search(): void {
    // First, check if there is still a shape in the URL query params
    // The shape URL params may have been removed by clearing the shape or drawing a new shape
    this._checkBoundingBoxParams();
    if (this.hasBoundingBoxParams) {
      // Show the bounding box overlay of complex shapes (ex. circle or polygon from URL query params)
      this._platesService.showBbox = true;
      this._platesService.toggleShapes(true);
    }

    // Close drawer if a plate is currently selected to prevent showing stale data
    if (this.selectedPlate !== null) {
      this.closeDetailsPanel();
    }

    if (this.sourceAndDeviceManagementEnabled) {
      this._removeDeepLink();
    } else {
      this._clearReadIdParams();
    }

    this.isLoading = true;
    this.hasLoaded = false;
    this._cd.markForCheck();
    this.page = 1;
    this.lprViewStagesComponent.startAtTop();
    this.pageSize = this.pageLimit * 2;

    // Mark boundary as searched if we have active shape parameters
    if (Object.keys(this._platesService.queriesSearchParams).length > 0) {
      this._platesService.hasBoundarySearched = true;
    }

    // SDM path: All providers search in parallel, results aggregated and filtered client-side
    if (this.sourceAndDeviceManagementEnabled) {
      const params = this._getAllSearchParams();
      this.hasSearched = true;

      this.providers.forEach((provider) => {
        provider.search(params);
        this._setProviderLoadedSubscription(provider);
      });

      this._trackSearchParams(params);
      this.didQuerySearch = true;
    } else {
      // Legacy path: Single provider at a time
      const additionalDetails = this.formModel.additionalDetails || [];
      const params: Partial<IFsLprParameters> = {
        searchReason: this.formModel.searchReason,
        plate: this.formModel.plate,
        fromDate: new Date(this.formModel.fromDate),
        toDate: new Date(this.formModel.toDate),
        recordType: this.formModel.hitsOnly ? RecordType.HIT : RecordType.ALL,
        plateState: this.formModel.plateState,
        vehicleColor: this.formModel.vehicleColor,
        vehicleMake: this.formModel.vehicleMake,
        vehicleBody: this.formModel.vehicleBody,
        vehicleName: this.formModel.vehicleName,
        assignedUserIds: this.formModel.assignedUserIds,
        deviceNames: this.formModel.deviceNames,
        caseNumber: this.formModel.caseNumber,
        includeSharedData: this.formModel.includeSharedData,
        limit: 100,
        sort: this.sort,
        pageSize: this.pageSize,
        inBbox:
          Object.keys(this._platesService.queriesSearchParams).length > 0
            ? this._platesService.queriesSearchParams
            : null,
        ...(additionalDetails.includes('topRack') && { topRack: true }),
        ...(additionalDetails.includes('windowStickers') && { windowStickers: true }),
        ...(additionalDetails.includes('bumperStickers') && { bumperStickers: true }),
        // Facets should be included each time the user clicks 'Search'
        includeFacets: true,
      };

      this.providers.forEach((provider) => {
        provider.search(params);

        provider.loaded$.pipe(takeUntil(this._destroy$)).subscribe({
          next: () => {
            this._updateResults();
            const anyProviderLoading = this.providers.find((provider) => provider.isLoading);
            this.isLoading = this.results.length === 0 && !!anyProviderLoading;
            this.hasLoaded = this.results.length > 0 || !anyProviderLoading;

            // 🆕 Clear map view search parameters when all providers finish loading
            if (!anyProviderLoading && this._platesService.hasMapViewSearched) {
              this._platesService.queriesSearchParams = {};
              this._platesService.hasMapViewSearched = false;
              this._platesService.mapInteracted = true;
            }

            this._updateExportButtonState();
            this._updateErrorsFromProviders();
            this._cd.detectChanges();
          },
        });
      });
      this.didQuerySearch = true;

      this._trackSearchParams(params);
    }
  }

  /**
   * Builds search parameters for SDM providers, including form values and camera filters.
   */
  private _getAllSearchParams(): Partial<IFsLprParameters> {
    if (!this.sourceAndDeviceManagementEnabled) {
      return {};
    }

    const additionalDetails = this.formModel.additionalDetails || [];
    const params: Partial<IFsLprParameters> = {
      searchReason: this.formModel.searchReason,
      plate: this.formModel.plate,
      fromDate: new Date(this.formModel.fromDate),
      toDate: new Date(this.formModel.toDate),
      recordType: this.formModel.hitsOnly ? RecordType.HIT : RecordType.ALL,
      plateState: this.formModel.plateState,
      vehicleColor: this.formModel.vehicleColor,
      vehicleMake: this.formModel.vehicleMake,
      vehicleBody: this.formModel.vehicleBody,
      vehicleName: this.formModel.vehicleName,
      assignedUserIds: this.formModel.assignedUserIds,
      deviceNames: this.formModel.deviceNames,
      caseNumber: this.formModel.caseNumber,
      includeSharedData: this.formModel.includeSharedData,
      limit: 100,
      sort: this.sort,
      pageSize: this.pageSize,
      inBbox:
        Object.keys(this._platesService.queriesSearchParams).length > 0
          ? this._platesService.queriesSearchParams
          : null,
      ...(additionalDetails.includes('topRack') && { topRack: true }),
      ...(additionalDetails.includes('windowStickers') && { windowStickers: true }),
      ...(additionalDetails.includes('bumperStickers') && { bumperStickers: true }),
      includeFacets: true,
    };

    return params;
  }

  /** Logs search parameters to analytics for tracking user search patterns and filter usage. */
  private _trackSearchParams(params: Partial<IFsLprParameters>): void {
    const searchedParams = Object.entries(params).reduce((acc, [key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as Partial<IFsLprParameters>);
    const toDate = this._timeService.convertToUnixTimestampSeconds(params.toDate);
    const fromDate = this._timeService.convertToUnixTimestampSeconds(params.fromDate);
    const dateRangeInDays = this._timeService.daysBetween(toDate, fromDate);
    const searchValueCount = Object.keys(searchedParams).length;
    const searchedItems = [...Object.keys(searchedParams)];
    this._mixpanelService.trackMetric(MxEventName.PerformLprSearchAf, {
      [MxPropertyName.SearchValuesCount]: searchValueCount,
      [MxPropertyName.SearchDateRange]: dateRangeInDays || -1,
      [MxPropertyName.SearchCriteria]: searchedItems,
    });
  }

  public cropHistoricalImages(historicalResults: FsLprFleetRead[]): void {
    if (historicalResults.length <= 0) {
      return;
    }

    // Reset the thumbnailImages array if it doesn't exist
    this.thumbnailImages = [];

    historicalResults.forEach((plate) => {
      const canvas = document.createElement('canvas');
      this._imageCropService
        .cropImage(plate, canvas, PlateImageCropService.DETAILS_DIMENSIONS, false)
        .then((croppedImage) => {
          this.thumbnailImages.push({
            croppedImage: croppedImage,
            plateId: plate.id,
          });
        });
    });
  }

  public findThumbnailImage(element: any): string {
    return (
      this.thumbnailImages.find((image) => image.plateId === element.id)?.croppedImage ||
      element.contextImageUrl
    );
  }

  /**
   *
   * @param selectedPlate The plate that was clicked
   * This method is used to open the right sidenav when a plate is clicked as well as
   * to set the selectedPlate variable to the plate that was clicked.
   */
  public onPlateClick(selectedPlate: IFsLprResult, isHistoricalPlate: boolean = false): void {
    this.selectedPlate = selectedPlate;
    if (!selectedPlate.isHit) {
      // Some models (e.g., LprQueryModel, LprFlockRead) have isHit as a getter-only property.
      // Attempting to set a getter-only property throws "Cannot set property isHit which has only a getter".
      // Check if the property is writable before attempting to set it.
      const descriptor =
        Object.getOwnPropertyDescriptor(selectedPlate, 'isHit') ||
        Object.getOwnPropertyDescriptor(Object.getPrototypeOf(selectedPlate), 'isHit');

      const isReadOnly = descriptor && descriptor.get && !descriptor.set;
      if (!isReadOnly) {
        this.selectedPlate.isHit = selectedPlate.type === this.HIT_TYPE;
      }
    }
    // If the selected plate was from a historical hit, the child component will update which dots
    // are green vs grey. Do not resize the drawer or re-run the historical plate search
    if (isHistoricalPlate) {
      return;
    }

    // Not from historical hit, reset historical results
    this.historicalResults = [];
    this.isHistoricalView = true;
    this._cd.detectChanges();

    const screenWidth = window.innerWidth;
    const columns = screenWidth >= 1200 ? 2 : 1;
    const optimalWidth = this.calculateOptimalDrawerWidth(columns);
    document.documentElement.style.setProperty('--drawer-width', `${optimalWidth}px`);

    if (!this.isDrawerOpen) {
      this.isDrawerOpen = true;

      // Call animateDrawerOpen without parameters
      this.animateDrawerOpen();

      // Wait for the drawer animation to complete before updating markers
      const resultsWrapper = this.resultsWrapperRef?.nativeElement as HTMLElement;
      if (resultsWrapper) {
        resultsWrapper.addEventListener(
          'transitionend',
          () => {
            this.plateMapComponent?.['_updateMarkers'](true);
          },
          { once: true },
        );
      } else {
        // Fallback if no resultsWrapper
        this.plateMapComponent?.['_updateMarkers'](true);
      }
    } else {
      const resultsWrapper = this.resultsWrapperRef?.nativeElement as HTMLElement;
      void resultsWrapper.offsetWidth;
      this.plateMapComponent?.['_updateMarkers'](true);
    }

    this.mapButtonText = SHOW_MAP;

    this._getHistoricalFleetPlates(selectedPlate);
  }

  public cancelSearch(): void {
    this.isLoading = false;
    this.hasLoaded = false;
    this.providers.forEach((provider) => {
      provider.cancel();
    });
  }

  /** Clears deep link params when performing a new search to prevent stale URLs. */
  private _clearReadIdParams(): void {
    const readId = this._activatedRoute.snapshot.queryParams.read_id;
    const timestamp = Number(this._activatedRoute.snapshot.queryParams.timestamp);
    const searchReason = this._activatedRoute.snapshot.queryParams.searchReason;
    if (readId && timestamp && searchReason) {
      this._router.navigate([], {
        queryParams: {
          read_id: null,
          timestamp: null,
          searchReason: null,
        },
        queryParamsHandling: 'merge',
      });
    }
  }

  private _getDeepLinkParams(): { readId: string; timestamp: number; searchReason: string } {
    const readId = this._activatedRoute.snapshot.queryParams.read_id;
    const timestamp = Number(this._activatedRoute.snapshot.queryParams.timestamp);
    const searchReason = this._activatedRoute.snapshot.queryParams.searchReason;
    return { readId, timestamp, searchReason };
  }

  private _trackDeepLinkSuccess(
    readId: string,
    timestamp: number,
    searchReason: string,
  ): void {
    this._dataDogRumService.addAction('lpr_read_loaded_deep_link', {
      read_id: readId,
      timestamp: timestamp,
      search_reason_length: searchReason?.length,
    });
  }

  private _trackDeepLinkFailure(
    readId: string,
    timestamp: number,
    searchReason: string,
  ): void {
    this._dataDogRumService.addAction('lpr_failed_to_load_read_deep_link', {
      read_id: readId,
      timestamp: timestamp,
      search_reason_length: searchReason?.length,
    });
  }

  private _markAllProvidersAsFinished(clearErrors: boolean = false): void {
    this.providers.forEach((provider) => {
      provider.isFinished = true;
      provider.isLoading = false;
      if (clearErrors) {
        provider.errors = [];
      }
    });
  }

  private _setProviderSingleResult(
    provider: IFsLprProviderInterface,
    read: IFsLprResult,
  ): void {
    provider.results = [read];
    provider.total = 1;
    provider.isFinished = true;
    provider.isLoading = false;
  }

  /*
   * SDM Deep Link - loads single read from external notification
   * isSdmDeepLinkActive flag locks provider/camera selection and pagination
   * until user performs new search or clears filters
   */
  private _checkForSdmDeepLink(): void {
    if (!this.sourceAndDeviceManagementEnabled) {
      return;
    }

    const { readId, timestamp, searchReason } = this._getDeepLinkParams();

    if (!(readId && timestamp && searchReason)) {
      return;
    }

    // Only Axon VI supports getReadById
    if (!this.vehicleIntelligenceEnabled) {
      return;
    }

    if (!this.providers.length) {
      return;
    }

    const axonProvider = this.providers[0];

    if (!axonProvider) {
      return;
    }

    if (axonProvider.name !== ViLPRProvider.AXON_VI) {
      return;
    }

    this.isSdmDeepLinkActive = true;

    this.formModel.searchReason = searchReason;

    this.isLoading = true;
    this.hasLoaded = false;
    this.hasSearched = false;

    // Type assertion: We've already verified this is an Axon provider with getReadById
    const providerWithGetReadById = axonProvider as IFsLprProviderInterface & {
      getReadById: (
        readId: string,
        timestamp: number,
        searchReason: string,
      ) => Observable<IFsLprResult>;
    };

    providerWithGetReadById
      .getReadById(readId, timestamp, searchReason)
      .pipe(
        tap((read) => {
          if (!read) {
            return;
          }

          this._setProviderSingleResult(axonProvider, read);
          axonProvider.errors = axonProvider.errors || [];

          this.activeProviders = [axonProvider];
          this._markAllProvidersAsFinished(true);

          this.page = 1;
          this.isLoading = false;
          this.hasLoaded = true;
          this.hasSearched = true;
          this.didQuerySearch = true;

          this._updateResults();
          this.onPlateClick(read as FsLprFleetRead);

          this._trackDeepLinkSuccess(readId, timestamp, searchReason);

          this._cd.markForCheck();
        }),
        catchError((error) => {
          console.error('Deep link single read failed', error);

          axonProvider.isFinished = true;
          axonProvider.isLoading = false;

          // Set hasLoaded=true to show error instead of infinite spinner
          this.isLoading = false;
          this.hasLoaded = true;
          this.results = [];

          this._trackDeepLinkFailure(readId, timestamp, searchReason);

          this._updateResults();
          this._cd.markForCheck();

          return of(null);
        }),
        take(1),
        takeUntil(this._destroy$),
      )
      .subscribe();
  }

  /*
   * Exits deep link mode - restores normal search state
   * Called on new search or clear filters to prevent deep link contamination
   */
  private _removeDeepLink(): void {
    if (!this.isSdmDeepLinkActive) {
      return;
    }

    this.animateDrawerClose();
    this.isSdmDeepLinkActive = false;

    this.cameraVendors = [];
    this.cameraModels = [];

    if (this.providers && this.providers.length > 0) {
      this.activeProviders = [...this.providers];

      this.providers.forEach((p) => {
        p.isFinished = false;
        p.isLoading = false;
      });
    }

    // Clear URL to prevent re-triggering on refresh
    this._router.navigate([], {
      queryParams: { read_id: null, timestamp: null, searchReason: null },
      queryParamsHandling: 'merge',
    });
  }

  /*
   * Non-SDM Deep Link - uses camera model to route read to correct provider tab
   */
  private _checkForReadId(): void {
    const readId = this._activatedRoute.snapshot.queryParams.read_id;
    const timestamp = Number(this._activatedRoute.snapshot.queryParams.timestamp);
    const searchReason = this._activatedRoute.snapshot.queryParams.searchReason;
    if (searchReason) {
      this.formModel.searchReason = searchReason;
    }
    const fleetProvider = this.providers.find((provider) => provider.name === 'Fleet 3');
    const outpostProvider = this.providers.find((provider) => provider.name === 'Outpost');
    const lightpostProvider = this.providers.find((provider) => provider.name === 'Lightpost');

    if (readId && timestamp && searchReason) {
      this._outpostLprProviderService
        .getReadById(readId, timestamp, searchReason)
        .pipe(takeUntil(this._destroy$))
        .subscribe(
          (read) => {
            this._markAllProvidersAsFinished();
            
            if (fleetProvider && CAMERA_MODELS.fleet_3.includes(read.cameraModel)) {
              this.activeProvider = fleetProvider;
              this._setProviderSingleResult(fleetProvider, read);
              this.hasLoaded = true;
              this._updateResults();
              this._cd.markForCheck();
            } else if (outpostProvider && CAMERA_MODELS.outpost.includes(read.cameraModel)) {
              this.activeProvider = outpostProvider;
              this._setProviderSingleResult(outpostProvider, read);
              this.hasLoaded = true;
              this._updateResults();
              this._cd.markForCheck();
            } else if (lightpostProvider && CAMERA_MODELS.lightpost.includes(read.cameraModel)) {
              this.activeProvider = lightpostProvider;
              this._setProviderSingleResult(lightpostProvider, read);
              this.hasLoaded = true;
              this._updateResults();
              this._cd.markForCheck();
            }
            
            this.onPlateClick(read);
            this._trackDeepLinkSuccess(readId, timestamp, searchReason);
          },
          () => {
            this._trackDeepLinkFailure(readId, timestamp, searchReason);
            this._markAllProvidersAsFinished();
            this.activeProvider = fleetProvider;
            fleetProvider.errors = [LPR_ERRORS.FAILED_TO_LOAD_READ];
            fleetProvider.total = 0;
            this.hasLoaded = true;
            this.isLoading = false;
            this.results = [];
            this._updateResults();
            this._cd.markForCheck();
          },
        );
    } else {
      this._dataDogRumService.addAction('lpr_invalid_read_deep_link', {
        read_id: readId,
        timestamp: timestamp,
        search_reason_length: searchReason?.length,
      });
    }
  }

  private _checkForPlateParam(): void {
    const plate = this._activatedRoute.snapshot.queryParams.plate;
    const fromAlert = this._activatedRoute.snapshot.queryParams.fromAlert;
    const searchReason = this._activatedRoute.snapshot.queryParams.searchReason;

    if (plate) {
      this.formModel.plate = plate;

      if (searchReason) {
        this.formModel.searchReason = searchReason;
      }
    }
  }

  private _setAlertDateRange(): void {
    const fromAlert = this._activatedRoute.snapshot.queryParams.fromAlert;

    if (fromAlert === 'true') {
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      this.formModel.fromDate = thirtyDaysAgo;
      this.formModel.toDate = now;

      // Update the form to reflect the model changes
      this.form.patchValue(this.formModel);
      this._cd.markForCheck();
    }
  }

  /**
   * Handles selection change on the historical plate search time dropdown
   * @param event The MatSelectChange event
   */
  public onHistoricalTimeChanged(event: MatSelectChange): void {
    this._selectedHistoricalDays = event.value;
    this._getHistoricalFleetPlates(this.selectedPlate);
  }

  /**
   * This method is used to fetch historical reads for a specific plate in the last numDays days
   * @param plate The plate that was clicked
   */
  private _getHistoricalFleetPlates(plate: IFsLprResult): void {
    // Use the selected days from dropdown
    const daysToUse = this._selectedHistoricalDays || this.defaultHistoricalSearchTimeOption;

    // Cancel any in-flight historical requests
    this._historicalRequestsCancel$.next();

    // Use current date/time as end date
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - daysToUse);

    const searchParams: Partial<IFsLprParameters> = {
      searchReason: this.formModel.searchReason,
      plate: plate.plate,
      fromDate: startDate,
      toDate: endDate,
      sort: PlateResultSort.DESC,
      includeSharedData: this.formModel.includeSharedData,
      // Below is a temporary fix for pagination, will update to consolidate
      // these fields into one as part of https://taserintl.atlassian.net/browse/FWX-1797
      limit: 200,
      pageSize: 200,
    };

    if (plate.displayState) {
      searchParams.plateState = plate.state;
    }

    this.isLoadingHistoricalResults = true;

    const providersToSearch = this.sourceAndDeviceManagementEnabled
      ? this.activeProviders
      : this.providers;

    const historicalSearchRequests$ = providersToSearch.map((provider) => {
      return provider.getHistoricalResults(searchParams);
    });
    forkJoin(historicalSearchRequests$)
      .pipe(takeUntil(this._historicalRequestsCancel$), takeUntil(this._destroy$))
      .subscribe({
        next: (responses) => {
          this.isLoadingHistoricalResults = false;
          // Combine all results from different providers
          const combinedResults = responses.map((response) => response?.results || []);
          const flattenedResults = flatten(combinedResults);
          this.historicalResults = flattenedResults;
          this.cropHistoricalImages(this.historicalResults as FsLprFleetRead[]);

          // Trigger map updates after getting new data
          this._cd.markForCheck();
          if (this.plateMapComponent) {
            // this.plateMapComponent.fitBounds(); // Removed to prevent map from zooming in on the historical results
            // Force markers update
            this.plateMapComponent['_updateMarkers']();
          }
          this._setHistoricalTableResults(plate);
        },
        error: (err) => {
          this.isLoadingHistoricalResults = false;
          console.error(err);
          this._toastrService.error(t('alpr.errors.failedToFetchReads'));
        },
      });

    // Initial results before the historical search is complete
    this._setHistoricalTableResults(plate);
  }

  /**
   * Adds date grouping for historical plate results
   * @param plate The plate that was clicked
   */
  private _setHistoricalTableResults(plate: IFsLprResult) {
    let lastDate = null;
    const ungroupedResults =
      this.historicalResults && this.historicalResults.length > 0
        ? this.historicalResults
        : [plate];
    const historicalResultsTableData = new MatTableDataSource(
      ungroupedResults.flatMap((resultItem) => {
        if (
          !resultItem.createdTimestamp ||
          isNaN(new Date(resultItem.createdTimestamp).getTime())
        ) {
          return [resultItem];
        }

        const readDate = new Date(resultItem.createdTimestamp);
        if (lastDate == null || lastDate != readDate.toDateString()) {
          lastDate = readDate.toDateString();
          const formatDatePipe = new FsFormatDatePipe(this._localeService);
          const formattedDate = formatDatePipe.transform(readDate, {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
          } as any);
          return [{ date: formattedDate }, resultItem];
        }
        return [resultItem];
      }),
    );

    this.tableResults = historicalResultsTableData;
    this._reverseGeocodeResults(historicalResultsTableData.data);
  }

  public isDateHeader(index, item) {
    return item.date;
  }
  /**
   * This method is used to format the coordinates
   * @param coordinates The coordinates to format in array format [latitude, longitude]
   * @returns The formatted coordinates in string format "latitude, longitude" or em dash if invalid
   */
  public formatCoordinates(coordinates: number[] | undefined): string {
    if (
      !coordinates ||
      coordinates.length !== 2 ||
      (coordinates[0] === 0 && coordinates[1] === 0)
    ) {
      return '\u2014'; // em dash
    }
    return `${coordinates[0]}, ${coordinates[1]}`;
  }

  /**
   * Reverse geocode coordinates for an array of results and log to console
   * @param results Array of plate results with coordinates
   */
  private _reverseGeocodeResults(results: any[]): void {
    this.pendingGeocodeRequests = 0;

    let resultsToGeocode = results;

    if (
      this.sourceAndDeviceManagementEnabled &&
      this.activeProviders &&
      this.activeProviders.length > 0
    ) {
      const activeProviderNames = this.activeProviders.map((p) => p.name);
      resultsToGeocode = results.filter((result) => {
        if (result.date) {
          return true;
        }
        const providerName = result.providerName || result.source;
        return providerName && activeProviderNames.includes(providerName);
      });
    }

    resultsToGeocode.forEach((result) => {
      const coordinates = result.geometry?.coordinates;
      // Use formatCoordinates to check if coordinates are valid (if it returns anything other than em dash)
      if (coordinates && this.formatCoordinates(coordinates) !== '\u2014') {
        // Track pending requests
        this.pendingGeocodeRequests++;

        this._searchAPI
          .reverseGeocode(coordinates, false)
          .pipe(takeUntil(this._destroy$))
          .subscribe({
            next: (geocodeResults) => {
              this.pendingGeocodeRequests--;
              // Store the geocoded address in the result object
              if (geocodeResults && geocodeResults.length > 0) {
                result.geocodedAddress = geocodeResults[0];

                // Refresh the table data to reflect the changes
                if (this.tableResults && this.pendingGeocodeRequests === 0) {
                  this.tableResults._updateChangeSubscription();
                  this._cd.markForCheck();
                }
              }
            },
            error: () => {
              this.pendingGeocodeRequests--;
            },
          });
      }
    });
  }

  /**
   * This method is used to generate form fields required for {@link FormlyForm}
   */
  private _genFormFields(): void {
    const quickSearchFields = [
      // Search reason field
      this._createSearchReasonField(),
      // Date/ Time Range fields
      this._createDateRangeButtonField(),
      this._createDateRangeDisplayField(),
    ];

    // Add case number field if Flock V3 is enabled
    if (this.flockV3Enabled) {
      quickSearchFields.splice(1, 0, this._createCaseNumberField());
    }

    if (this.vehicleIntelligenceEnabled && this.lprSharingEnabled) {
      quickSearchFields.splice(1, 0, this._createIncludeSharedDataField());
    }

    this.formFields = [
      {
        templateOptions: { label: t('alpr.form.quickSearch') },
        fieldGroup: quickSearchFields,
        wrappers: ['form-field-group'],
      },
      {
        id: 'vehicle-details',
        templateOptions: { label: t('alpr.hotlist.vehicleDetails.title') },
        fieldGroup: [
          ...this._createVehicleDetailsFields(),
          ...this._createAdditionalDetailsFields(),
        ],
        wrappers: ['form-field-group'],
      },
      {
        id: 'device-details',
        templateOptions: { label: t('alpr.deviceDetails') },
        fieldGroup: this._createDeviceDetailsFields(),
        wrappers: ['form-field-group'],
        hide: !this.vehicleIntelligenceEnabled,
      },
      {
        templateOptions: { label: t('alpr.form.hitsReads') },
        fieldGroup: [this._createHitReadCategoryField()],
        wrappers: ['form-field-group'],
      },
    ];
  }

  /**
   * This method is used to create the search reason form field.
   * @returns {SnFormFieldConfig} which is the search reason form field.
   */
  private _createSearchReasonField(): SnFormFieldConfig {
    return {
      key: 'searchReason',
      type: INPUT_TYPE.INPUT,
      templateOptions: {
        ...baseFieldConfig,
        label: t('alpr.form.reason'),
        required: true,
        minLength: MIN_REASON_LENGTH,
      },
      validation: {
        messages: {
          required: () => t('alpr.validation.reasonRequired'),
          minlength: (error, field) =>
            t('alpr.validation.reasonMinLength', { minLength: field.templateOptions.minLength }),
        },
      },
    };
  }

  /**
   * This method is used to create the include shared data form field.
   * @returns {SnFormFieldConfig}
   */
  private _createIncludeSharedDataField(): SnFormFieldConfig {
    return {
      key: 'includeSharedData',
      type: 'toggle-tooltip',
      templateOptions: {
        label: t('vehicleIntelligence.alprSearch.form.sharedRecords.includeSharedDataLabel'),
        tooltipText: t('vehicleIntelligence.alprSearch.form.sharedRecords.includeSharedDataTooltip'),
        defaultValue: false,
      },
    };
  }

  /**
   * This method is used to create the case number form field.
   * @returns {SnFormFieldConfig} which is the case number form field.
   */
  private _createCaseNumberField(): SnFormFieldConfig {
    return {
      key: 'caseNumber',
      type: INPUT_TYPE.INPUT,
      templateOptions: {
        ...baseFieldConfig,
        label: t('common.caseNumber'),
        placeholder: t('vehicleIntelligence.alprSearch.form.caseNumber.placeholder'),
        required: false,
      },
    };
  }

  /**
   * Used to create the date range buttons field.
   * @returns {SnFormFieldConfig} which are the time range form fields.
   */
  private _createDateRangeButtonField(): SnFormFieldConfig {
    return {
      key: 'date-range-buttons',
      type: 'date-range-picker-v2',
      templateOptions: {
        startTimestampKey: 'event_timestamp_from',
        endTimestampKey: 'event_timestamp_to',
        defaultRange:
          this._activatedRoute.snapshot.queryParams.fromAlert === 'true' ? null : '2 hrs',
        custom: true,
        style: Styles.Button,
        required: true,
        ranges: [
          { value: '2', mode: Modes.Hour, label: t('alpr.timeRangeOptions.2hours') },
          { value: '1', mode: Modes.Day, label: t('alpr.timeRangeOptions.24hours') },
          { value: '2', mode: Modes.Day, label: t('alpr.timeRangeOptions.48hours') },
        ],
        extendedRanges: [
          { value: '7', mode: Modes.Day, label: t('alpr.historicalSearchTimeOptions.last7Days') },
          { value: '14', mode: Modes.Day, label: t('alpr.historicalSearchTimeOptions.last14Days') },
          { value: '30', mode: Modes.Day, label: t('alpr.historicalSearchTimeOptions.last30Days') },
        ],
        change: (field, value) => {
          const fromDate = value['event_timestamp_from'];
          const toDate = value['event_timestamp_to'];
          this.formModel.fromDate = new Date(fromDate * 1000);
          this.formModel.toDate = new Date(toDate * 1000);
          this.totalDays = this._timeService.daysBetween(fromDate, toDate);
          this._cd.markForCheck();
          const dateDisplayField = field.parent.fieldGroup.find(
            (field) => field.key === 'date-range-display',
          );
          if (dateDisplayField?.formControl) {
            dateDisplayField.formControl.updateValueAndValidity();
          }
        },
      },
      expressionProperties: {
        'templateOptions.disabled': () =>
          !this.form.value.search_reason || this.form.value.search_reason?.length < 4,
      },
    };
  }

  /**
   * Used to create the date range display field.
   * @returns {SnFormFieldConfig} which are the time range form fields.
   */
  private _createDateRangeDisplayField(): SnFormFieldConfig {
    return {
      key: 'date-range-display',
      type: 'date-range-picker-v2',
      templateOptions: {
        startTimestampKey: 'event_timestamp_from',
        endTimestampKey: 'event_timestamp_to',
        appearance: 'outline',
        label: t('alpr.form.timeRange'),
        style: Styles.Dropdown,
        required: true,
        ranges: [],
        change: (field, value) => {
          const fromDate = value['event_timestamp_from'];
          const toDate = value['event_timestamp_to'];
          this.formModel.fromDate = new Date(fromDate * 1000);
          this.formModel.toDate = new Date(toDate * 1000);
          this.totalDays = this._timeService.daysBetween(toDate, fromDate);
          if (this._datepickerInitialized) {
            const dateDisplayField = field.parent.fieldGroup.find(
              (field) => field.key === 'date-range-buttons',
            );
            dateDisplayField.templateOptions.clearToggles();
          }
          this._datepickerInitialized = true;
          this._cd.markForCheck();
        },
        getRangeMessage: () => {
          const start = new Date(this.formModel.fromDate);
          const end = new Date(this.formModel.toDate);
          const formatted = `${format(start, 'MM/dd/yy HH:mm')} - ${format(end, 'MM/dd/yy HH:mm')}`;
          return formatted;
        },
      },
      expressionProperties: {
        'templateOptions.disabled': () =>
          !this.form.value.search_reason || this.form.value.search_reason?.length < 4,
      },
    };
  }

  /**
   * Used to create the Hit / Read Category field.
   * @returns Returns the hit /read category field of type {@link SnFormFieldConfig}
   */
  private _createHitReadCategoryField(): SnFormFieldConfig {
    return {
      fieldGroup: [
        {
          className: 'toggle-label-text',
          template: `<div class="toggle-label">${t('alpr.form.hitsOnly')}</div>`,
        },
        {
          key: 'hitsOnly',
          type: INPUT_TYPE.TOGGLE,
          defaultValue: false, // Toggle: false = ALL (default)
          className: 'toggle-switch-only',
          templateOptions: {
            hideLabel: true, // Hide the default label
            appearance: 'none', // Remove border/outline
          },
        },
      ],
      fieldGroupClassName: 'toggle-field-container', // This is the key property for fieldGroup styling
      expressionProperties: {},
    };
  }

  /**
   * Used to create the vehicle details fields.
   * @returns Returns the vehicle details fields of type {@link SnFormFieldConfig}
   */
  private _createVehicleDetailsFields(): SnFormFieldConfig[] {
    return [
      {
        key: 'plate',
        type: INPUT_TYPE.INPUT,
        templateOptions: {
          ...baseFieldConfig,
          label: t('alpr.form.licensePlate'),
          placeholder: t('alpr.form.licensePlate'),
          required: false,
        },
        expressionProperties: {},
      },
      {
        key: 'plateState',
        type: INPUT_TYPE.SELECT,
        templateOptions: {
          ...baseFieldConfig,
          label: t('alpr.hotlist.vehicleDetails.plateState'),
          placeholder: t('alpr.hotlist.vehicleDetails.plateState'),
          required: false,
          options: US_STATES,
          emptyValueIsNull: true,
        },
        expressionProperties: {},
      },
      {
        key: 'vehicleColor',
        type: INPUT_TYPE.SELECT_SEARCH,
        templateOptions: {
          ...baseFieldConfig,
          label: t('common.color'),
          selectAllOption: t('common.selectAll'),
          clearAllButton: true,
          placeholder: t('common.color'),
          options: VEHICLE_COLOR,
          multiple: true,
        },
        expressionProperties: {},
      },
      {
        key: 'vehicleMake',
        type: INPUT_TYPE.SELECT_SEARCH,
        templateOptions: {
          ...baseFieldConfig,
          label: t('alpr.hotlist.vehicle.make'),
          placeholder: t('alpr.hotlist.vehicle.make'),
          options: VEHICLE_MAKE,
        },
        expressionProperties: {},
      },
      {
        key: 'vehicleBody',
        type: INPUT_TYPE.SELECT_SEARCH,
        templateOptions: {
          ...baseFieldConfig,
          label: t('common.type'),
          placeholder: t('common.type'),
          options: VEHICLE_BODY_TYPE,
        },
        expressionProperties: {},
      },
    ];
  }

  /**
   * Used to create the additional details fields with checkboxes for vehicle attributes.
   * @returns Returns the additional details fields of type {@link SnFormFieldConfig}
   */
  private _createAdditionalDetailsFields(): SnFormFieldConfig[] {
    return [
      {
        key: 'additionalDetails',
        type: INPUT_TYPE.SELECT_SEARCH,
        templateOptions: {
          ...baseFieldConfig,
          label: t('vehicleIntelligence.alprSearch.form.additionalDetails.label'),
          placeholder: t('vehicleIntelligence.alprSearch.form.additionalDetails.placeholder'),
          clearAllButton: true,
          multiple: true,
          options: [
            {
              label: t('vehicleIntelligence.alprSearch.form.additionalDetails.topRack'),
              value: 'topRack',
            },
            {
              label: t('vehicleIntelligence.alprSearch.form.additionalDetails.windowStickers'),
              value: 'windowStickers',
            },
            {
              label: t('vehicleIntelligence.alprSearch.form.additionalDetails.bumperStickers'),
              value: 'bumperStickers',
            },
          ],
        },
        expressionProperties: {},
      },
    ];
  }

  private _createDeviceDetailsFields(): SnFormFieldConfig[] {
    return [
      {
        key: 'assignedUserIds',
        type: INPUT_TYPE.SELECT_SEARCH,
        templateOptions: {
          ...baseFieldConfig,
          label: t('alpr.assignedUser'),
          placeholder: t('common.user'),
          multiple: true,
          clearAllButton: true,
          options: this._getUsersList(),
        },
        expressionProperties: {
          'templateOptions.disabled': () => this._shouldDisableDeviceFields(),
        },
      },
      {
        key: 'deviceNames',
        type: INPUT_TYPE.SELECT_SEARCH,
        templateOptions: {
          ...baseFieldConfig,
          label: t('alpr.deviceName'),
          placeholder: t('alpr.deviceName'),
          multiple: true,
          clearAllButton: true,
          skipFilteringOnInputChange: true,
          options: this._getDeviceNames(''),
          inputChange: (field: FormlyFieldConfig, value: string) => {
            if (field?.templateOptions) {
              field.templateOptions.options = this._getDeviceNames(value || '');

              this._cd.detectChanges();
              field.templateOptions.updateOptions?.();
            }
          },
        },
        expressionProperties: {
          'templateOptions.disabled': () => this._shouldDisableDeviceFields(),
        },
      },
    ];
  }

  private _shouldDisableDeviceFields(): boolean {
    if (this.sourceAndDeviceManagementEnabled) {
      return false;
    }

    return (
      this.activeProvider &&
      this.activeProvider.name !== 'Fleet 3' &&
      this.activeProvider.name !== 'Outpost' &&
      this.activeProvider.name !== 'Lightpost'
    );
  }

  private _getUsersList(): Observable<{ label: string; value: string }[]> {
    if (this.vehicleIntelligenceEnabled) {
      return this._axonVILprService.getUsers().pipe(
        map((response) => response.map(this._mapUserOption)),
        shareReplay(1),
      );
    }

    return of([]);
  }

  private _mapUserOption(user: FsLprAxonVIUser): { label: string; value: string } {
    return {
      label: user.displayName,
      value: user.id,
    };
  }

  private _getDeviceNames(searchTerm: string): Observable<{ label: string; value: string }[]> {
    if (this.vehicleIntelligenceEnabled) {
      return this._axonVILprService
        .getCameraNameSuggestions(searchTerm)
        .pipe(map((response) => response.map(this._mapCameraNameOption)));
    }

    return of([]);
  }

  private _mapCameraNameOption(suggestion: FsLprAxonVIFilterSuggestion): {
    label: string;
    value: string;
  } {
    return {
      label: suggestion.value,
      value: suggestion.value,
    };
  }

  public animateDrawerOpen(): void {
    const contentArea = this.contentAreaRef?.nativeElement as HTMLElement;
    const resultsWrapper = this.resultsWrapperRef?.nativeElement as HTMLElement;

    if (resultsWrapper && contentArea) {
      this.isDrawerOpen = true;
      this._renderer.addClass(resultsWrapper, 'open');
      this._renderer.addClass(contentArea, 'drawer-open');
    }
  }

  public animateDrawerClose(): void {
    const contentArea = this.contentAreaRef?.nativeElement as HTMLElement;
    const resultsWrapper = this.resultsWrapperRef?.nativeElement as HTMLElement;

    if (resultsWrapper && contentArea) {
      this.isDrawerOpen = false;
      this._renderer.removeClass(resultsWrapper, 'open');
      this._renderer.removeClass(contentArea, 'drawer-open');
    }
  }

  private _updateMapAfterOpen(): void {
    if (this.plateMapComponent) {
      this.plateMapComponent.fitBounds();
      this.plateMapComponent['_updateMarkers']();
    }
  }

  private cleanupAfterClose(): void {
    this.selectedPlate = null;
    this.isHistoricalView = false;
    if (this.lprViewStagesComponent) {
      this.lprViewStagesComponent.selectedPlate = null;
      this.lprViewStagesComponent.selectedCardIndex = -1;
    }

    this._cd.markForCheck();
  }

  public showMap = (): void => {
    const resultsWrapper = this.resultsWrapperRef?.nativeElement as HTMLElement;
    if (!resultsWrapper) return;

    // Reset map state at the beginning to clear any previous popups
    this.plateMapComponent?.resetMapState();

    if (this.selectedPlate !== null) {
      this.selectedPlate = null;
      this.isHistoricalView = false;

      if (this.lprViewStagesComponent) {
        this.lprViewStagesComponent.selectedPlate = null;
        this.lprViewStagesComponent.selectedCardIndex = -1;
      }

      this.mapButtonText = HIDE_MAP;
      this._cd.markForCheck();

      if (this.plateMapComponent) {
        this.plateMapComponent.fitBounds();
        this.plateMapComponent['_updateMarkers']();
      }

      return;
    }

    if (!this.isDrawerOpen) {
      this.isDrawerOpen = true;

      // Force a reflow
      void resultsWrapper.offsetWidth;

      // Start the animation in the next frame
      requestAnimationFrame(() => {
        this._cd.markForCheck();

        resultsWrapper.addEventListener(
          'transitionend',
          () => {
            this._updateMapAfterOpen();
            this.mapButtonText = HIDE_MAP;
            this._cd.markForCheck();
          },
          { once: true },
        );
      });
    } else {
      // Start closing animation
      this.isDrawerOpen = false;

      resultsWrapper.addEventListener(
        'transitionend',
        () => {
          this.cleanupAfterClose();
          this.mapButtonText = SHOW_MAP;
          this._cd.markForCheck();
        },
        { once: true },
      );
    }
  };

  public onResizeStart(event: MouseEvent | TouchEvent): void {
    if (this.selectedPlate !== null || !this.resultsWrapperRef) return;

    this.isResizing = true;
    this.startWidth = this.resultsWrapperRef.nativeElement.offsetWidth;
    this.startX = event instanceof MouseEvent ? event.pageX : event.touches[0].pageX;

    document.body.style.userSelect = 'none';

    this.resultsWrapperRef.nativeElement.classList.add('resizing');

    event.preventDefault();
    event.stopPropagation();
  }

  public onResizeEnd = (event: MouseEvent | TouchEvent): void => {
    if (!this.isResizing) return;

    this.isResizing = false;
    document.body.style.userSelect = '';
    this.resultsWrapperRef.nativeElement.classList.remove('resizing');

    // Use the current width directly instead of recalculating an "optimal" width
    // This preserves the exact width the user dragged to
    const containerWidth = this.contentAreaRef.nativeElement.offsetWidth;
    const firstColMin = AlprReadsSearchComponent.DRAWER_CONFIG.minWidth;
    const minAllowedWidth = this.calculateOptimalDrawerWidth(1);
    const maxAllowedWidth = containerWidth - firstColMin;

    // Ensure width is within bounds
    const finalWidth = Math.max(
      minAllowedWidth,
      Math.min(this._currentCalculatedWidth, maxAllowedWidth),
    );

    // Apply the width directly
    document.documentElement.style.setProperty('--drawer-width', `${finalWidth}px`);

    requestAnimationFrame(() => {
      if (this.plateMapComponent) {
        this.plateMapComponent.resetMapState();
        this.plateMapComponent.fitBounds();
        this.plateMapComponent['_updateMarkers']();
      }
      this._cd.markForCheck();
    });

    event.preventDefault();
    event.stopPropagation();
  };

  private _onResizeMove = (event: MouseEvent | TouchEvent): void => {
    if (!this.isResizing || this.selectedPlate !== null) return;

    if (this._resizeRAF !== null) {
      cancelAnimationFrame(this._resizeRAF);
    }

    this._resizeRAF = requestAnimationFrame(() => {
      let currentX: number;
      if (event instanceof MouseEvent) {
        currentX = event.pageX;
      } else {
        currentX = event.touches[0].pageX;
      }

      const diff = this.startX - currentX;
      const newWidth = this.startWidth + diff;

      const containerWidth = this.contentAreaRef.nativeElement.offsetWidth;

      const firstColMin = AlprReadsSearchComponent.DRAWER_CONFIG.minWidth; // 400
      const maxAllowedWidth = containerWidth - firstColMin;
      const minAllowedWidth = this.calculateOptimalDrawerWidth(1);

      // Store the calculated width for use in onResizeEnd
      this._currentCalculatedWidth = Math.max(minAllowedWidth, Math.min(newWidth, maxAllowedWidth));

      // Update the drawer width in real-time
      document.documentElement.style.setProperty(
        '--drawer-width',
        `${this._currentCalculatedWidth}px`,
      );

      // Update the view if needed
      this._cd.detectChanges();
    });

    event.preventDefault();
    event.stopPropagation();
  };

  /**
   * This method is used to close the details panel and the right sidenav when the lightbox is closed in the vehicle
   * details panel.
   */
  public closeDetailsPanel(plate?: FsLprFleetRead): void {
    if (plate) {
      this.closedPlateDetails$.next(plate);
    }

    const resultsWrapper = this.resultsWrapperRef?.nativeElement as HTMLElement;
    if (!resultsWrapper) return;

    this.animateDrawerClose();
    this.cleanupAfterClose();
    this.mapButtonText = SHOW_MAP;
  }

  /**
   * This method is called when the user clicks the "Search for this plate" button in the details view
   * It sets the plate and state parameters and triggers a new search
   * @param plateData The plate data containing plate number and state
   */
  public onSearchSpecificPlate(plateData: { plate: string; state?: string }): void {
    if (!this.formModel.searchReason || this.formModel.searchReason.length < MIN_REASON_LENGTH) {
      return;
    }

    // Validate state before setting it
    let validState = null;
    if (plateData.state) {
      // Check if the provided state exists in US_STATES
      const stateExists = US_STATES.some(
        (state) => state.value.toLowerCase() === plateData.state?.toLowerCase(),
      );

      validState = stateExists ? plateData.state : null;
    }

    // Update the form model with the plate data
    this.formModel = {
      ...this.formModel,
      plate: plateData.plate,
      plateState: validState,
      vehicleColor: null,
      vehicleMake: null,
      vehicleBody: null,
      vehicleName: null,
      assignedUserIds: null,
      deviceNames: null,
      additionalDetails: null,
      fromDate: this.formModel.fromDate,
      toDate: this.formModel.toDate,
      searchReason: this.formModel.searchReason,
      includeSharedData: this.formModel.includeSharedData,
    };

    // Close the details panel
    this.closeDetailsPanel();

    // Reset to initial view state
    this.isHistoricalView = false;
    this.selectedPlate = null;

    // Execute the search
    this.search();
  }

  /**
   * This method is called when the user changes the tab
   * If we change to the Map tab, we need to fit the bounds and update the markers
   * @param event is the {@link MatTabChangeEvent} object
   */
  public onTabChange(event: MatTabChangeEvent): void {
    // event.index will be 0 for Map tab, 1 for Read List tab
    if (event.tab.textLabel === this.MAP_TAB_LABEL && this.plateMapComponent) {
      this.plateMapComponent.fitBounds();
      this.plateMapComponent['_updateMarkers']();
    }
  }

  /**
   * This method is used to clear the errors array.
   */
  private _clearErrors(): void {
    this.errors = [];
  }

  /**
   * This method is used to update the errors and warnings from the providers.
   * It checks if there is an active provider and updates the errors and warnings from it.
   * If there is no active provider, it updates only the errors from all providers (not warnings).
   */
  private _updateErrorsFromProviders(): void {
    if (this.activeProvider) {
      // Only show errors and warnings for the selected provider
      this.errors = this.activeProvider.errors || [];
      this.warnings = this.activeProvider.warnings || [];
    } else {
      // When no specific provider is selected, don't show any errors
      // This prevents showing all errors from all providers at once
      this.errors = [];
      this.warnings = [];
    }
  }

  // #region Source & Device Management

  /** Loads all available providers based on enabled integrations and initializes their connections. */
  private _initializeProviders(): void {
    this.providers = this._lprSourceManagerService.getAvailableProviders();

    this.providers.forEach((provider) => {
      provider.init();
    });
  }

  /** Sets up subscriptions to watch all provider load states for UI updates. */
  private _setupProviderLoadedSubscription(): void {
    this.providers.forEach((provider) => {
      this._setProviderLoadedSubscription(provider);
    });
  }

  /**
   * Watches a provider's load state to update UI and manage result aggregation.
   * Triggers UI refresh on each provider update, showing results progressively as they arrive.
   */
  private _setProviderLoadedSubscription(provider: IFsLprProviderInterface): void {
    // Unsubscribe from previous subscription for this provider if it exists
    const existingSubscription = this._providerLoadedSubscriptions.get(provider);
    if (existingSubscription) {
      existingSubscription.unsubscribe();
    }

    const subscription = provider.loaded$
      .pipe(
        takeUntil(this._destroy$),
        tap(() => {
          this._updateResults();
          const anyProviderLoading = this.providers.find((provider) => provider.isLoading);
          this.isLoading = this.results.length === 0 && !!anyProviderLoading;
          this.hasLoaded = this.results.length > 0 || !anyProviderLoading;

          // Clear map view search parameters when all providers finish loading
          if (!anyProviderLoading && this._platesService.hasMapViewSearched) {
            this._platesService.queriesSearchParams = {};
            this._platesService.hasMapViewSearched = false;
            this._platesService.mapInteracted = true;
          }

          this._updateExportButtonState();
          this._updateErrorsFromProviders();
          this._cd.detectChanges();
        }),
      )
      .subscribe();

    // Store the subscription so we can unsubscribe later
    this._providerLoadedSubscriptions.set(provider, subscription);
  }

  private _getProvider(name: ViLPRProvider): IFsLprProviderInterface | undefined {
    return this.providers.find((p) => p.nameEnum === name);
  }

  /**
   * Shows results from a provider. Uses cached results if available for instant display.
   * Provider pre-fetches all data on search, so selection just toggles visibility.
   */
  private _selectProvider(name: ViLPRProvider): void {
    const provider = this._getProvider(name);
    if (!provider) {
      return;
    }

    const isActiveProvider = this.activeProviders.some((p) => p.nameEnum === provider.nameEnum);
    const hasResults = provider.results.length > 0;

    if (!isActiveProvider && hasResults) {
      this._activateProvider(provider);
      this._updateResults();
      return;
    }

    if (!isActiveProvider) {
      this._activateProvider(provider);
    }

    this._updateResults();
  }

  /**
   * Hides results from a provider.
   * WWA and Axon providers are handled via camera model selection, not directly.
   */
  private _deselectProvider(name: ViLPRProvider): void {
    const isWWA = name === ViLPRProvider.WORKS_WITH_AXON;

    // WWA isn't a real provider - camera model selection controls its visibility
    if (isWWA) {
      return;
    }

    const isAllAxon = name === ViLPRProvider.AXON_VI;

    // Axon providers are controlled by camera model selection to allow granular filtering
    if (isAllAxon) {
      return;
    }

    const provider = this._getProvider(name);
    if (!provider) {
      return;
    }

    this._deactivateProvider(provider);

    this._updateResults();
  }

  /** Adds a provider to the active list, making its results visible. */
  private _activateProvider(provider: IFsLprProviderInterface): void {
    if (!provider) {
      return;
    }

    const isAlreadyActive = this.activeProviders.some((p) => p.nameEnum === provider.nameEnum);
    if (!isAlreadyActive) {
      this.activeProviders.push(provider);
    }
  }

  /**
   * Removes a provider from the active list, hiding its results.
   * Axon providers use camera model selection instead of direct deactivation.
   */
  private _deactivateProvider(provider: IFsLprProviderInterface): void {
    if (
      provider &&
      (provider.nameEnum === ViLPRProvider.AXON_VI)
    ) {
      return;
    }

    this.activeProviders = this.activeProviders.filter((p) => p.nameEnum !== provider.nameEnum);
  }

  /**
   * Collects results from providers when SDM is enabled
   * Handles multiple active providers with error/warning filtering and camera model filtering
   */
  private _collectProviderResultsSdm(): IFsLprResult[] {
    // If no providers are active, return empty results
    // This happens when user deliberately unchecks all providers
    if (!this.activeProviders.length) {
      return [];
    }

    const results = this.activeProviders
      .map((provider) => {
        const hasErrors = provider.errors && provider.errors.length > 0;
        const hasWarnings = provider.warnings && provider.warnings.length > 0;

        // Do not show results if there are errors
        if (hasErrors) {
          return [];
        }

        // Results can exist even if there are warnings - show them if configured to do so
        if (hasWarnings) {
          const filteredResults = this.shouldShowPartialMatches ? provider.results : [];
          return filteredResults;
        }

        let providerResults = provider.results || [];

        // Filter Axon VI results by selected camera models
        if (this.shouldFilterResultsByCameraModel(provider)) {
          providerResults = this.filterResultsByCameraModel(providerResults);
        }

        return providerResults;
      })
      .flat();

    return results;
  }

  /**
   * Determines if camera model filtering should be applied for a given provider
   * Filtering is only applied to Axon VI providers when camera models are selected
   */
  private shouldFilterResultsByCameraModel(provider: IFsLprProviderInterface): boolean {
    return (
      provider.nameEnum === ViLPRProvider.AXON_VI &&
      this.cameraModels &&
      this.cameraModels.length > 0
    );
  }

  /**
   * Filters results by selected camera models
   * Only includes results where the camera model matches one of the selected models
   */
  private filterResultsByCameraModel(results: IFsLprResult[]): IFsLprResult[] {
    return results.filter((result) => {
      const cameraModel = (result as FsLprAxonVIRead).cameraModel;
      return cameraModel && this.cameraModels.includes(cameraModel);
    });
  }

  // #endregion Source & Device Management

  /**
   * This method is used to collect results from all providers.
   * It checks if there is an active provider and collects results from it.
   * If there is no active provider, it collects results from all providers that don't have warnings.
   */
  private _collectProviderResults(): IFsLprResult[] {
    return this.sourceAndDeviceManagementEnabled
      ? this._collectProviderResultsSdm()
      : this._collectProviderResultsStandard();
  }

  /**
   * Collects results from providers in standard mode (SDM disabled)
   * Uses either the single active provider or all providers without errors/warnings
   */
  private _collectProviderResultsStandard(): IFsLprResult[] {
    if (this.activeProvider) {
      return this.activeProvider.results;
    } else {
      // Only show results from providers that don't have warnings
      return this.providers
        .filter(
          (provider) =>
            (!provider.warnings || provider.warnings.length === 0) &&
            (!provider.errors || provider.errors.length === 0),
        )
        .map((provider) => provider.results)
        .flat();
    }
  }

  /**
   * This method is used to load more results if needed.
   * It checks if we need to load more results to maintain sufficient buffer for upcoming pages.
   *
   * @param resultsBuffer The number of results to buffer
   * @param currentIndexOfResults The current index of the results
   */
  private _loadMoreResultsIfNeeded(resultsBuffer: number, currentIndexOfResults: number): void {
    // Calculate the number of results needed for the next page(s)
    // We need to ensure we have enough results for the current page plus a buffer
    const neededResultsCount = currentIndexOfResults + resultsBuffer;

    if (this.sourceAndDeviceManagementEnabled) {
      // Don't paginate during deep link - we only want the single result
      if (this.isSdmDeepLinkActive) {
        return;
      }

      // Check raw results count (before filtering) to avoid over-fetching
      // when camera model filtering is active
      const providersToUse = this.activeProviders.length ? this.activeProviders : this.providers;
      const rawResultsCount = providersToUse.reduce((sum, provider) => {
        // Skip providers with errors as they won't contribute results
        if (provider.errors && provider.errors.length > 0) {
          return sum;
        }
        return sum + (provider.results?.length || 0);
      }, 0);

      const needMoreResults = rawResultsCount < neededResultsCount;

      // Only load more if there aren't enough raw results for the next page(s)
      if (!needMoreResults) return;

      // Prevent infinite loop: don't trigger more loads if any provider is already loading
      const anyProviderLoading = this.activeProviders.some((p) => p.isLoading);
      if (anyProviderLoading) return;

      this.activeProviders.forEach((provider) => {
        if (!provider.isFinished && !provider.isLoading && !provider.errors?.length) {
          provider.next();
        }
      });
    } else {
      const results = this._collectProviderResults();
      const needMoreResults = results.length < neededResultsCount;

      // Only load more if there aren't enough results for the next page(s)
      if (!needMoreResults) return;

      if (this.activeProvider) {
        if (!this.activeProvider.isFinished && !this.activeProvider.isLoading) {
          this.activeProvider.next();
        }
      } else {
        this.providers.forEach((provider) => {
          if (!provider.isFinished && !provider.isLoading) {
            provider.next();
          }
        });
      }
    }
  }

  /**
   * This method is used to sort the results by timestamp.
   * It sorts the results in ascending or descending order based on the sort property.
   *
   * @param results The results to be sorted
   * @returns The sorted results
   */
  private _sortResultsByTimestamp(results: IFsLprResult[]): IFsLprResult[] {
    return [...results].sort((a, b) => {
      return this.sort === PlateResultSort.ASC
        ? new Date(a.createdTimestamp).getTime() - new Date(b.createdTimestamp).getTime()
        : new Date(b.createdTimestamp).getTime() - new Date(a.createdTimestamp).getTime();
    });
  }

  /**
   * This method is used to paginate the results.
   * It takes the current page and the page limit into account.
   *
   * @param results The results to be paginated
   * @param page The current page number
   * @returns
   */
  private _paginateResults(results: IFsLprResult[]): IFsLprResult[] {
    const startIndex = (this.page - 1) * this.pageLimit;
    const endIndex = this.page * this.pageLimit;
    return results.slice(startIndex, endIndex);
  }

  private _updateVisualization(): void {
    // Ensure map component gets updated results first
    this._cd.detectChanges();

    if (this.plateMapComponent) {
      // Check if any provider is still loading
      const anyProviderLoading = this.providers.find((provider) => provider.isLoading);

      // Only auto-zoom if triggered by "Search" button click AND all providers are done
      if (this._shouldAutoZoom && !anyProviderLoading) {
        this.plateMapComponent.fitBounds();
        // Reset flag after auto-zoom
        this._shouldAutoZoom = false;
      }
      this.plateMapComponent['_updateMarkers']();
    }
  }

  /**
   * Updates the total results count from all providers
   */
  private _updateTotalResultsCount(): void {
    if (this.activeProvider) {
      // If there's an active provider, use only its totals
      this.totalResultsCount = {
        total: this.activeProvider.total,
        totalQueryResults: this.activeProvider.total,
      };
    } else {
      // Sum totals from all providers that don't have errors
      const validProviders = this.providers.filter(
        (provider) => !provider.errors || provider.errors.length === 0,
      );

      const total = validProviders.reduce((sum, provider) => sum + (provider.total || 0), 0);

      this.totalResultsCount = {
        total: total,
        totalQueryResults: total,
      };
    }
  }

  /**
   * This method is used to update the results based on the current page and provider.
   * It collects results from all providers and sorts them by timestamp.
   * It also handles pagination and updates the visualization.
   */
  private _updateResults(): void {
    let newResults = [];
    const nProviders = this.activeProvider ? 1 : this.providers.length;
    const resultsBuffer = this.pageLimit * nProviders;
    const currentIndexOfResults = this.page * this.pageLimit;

    if (this.isReviewingPlates) {
      newResults = this.reviewResults;
      this._clearErrors();
    } else {
      newResults = this._collectProviderResults();
      this._loadMoreResultsIfNeeded(resultsBuffer, currentIndexOfResults);
      this._updateErrorsFromProviders();
    }

    /*
     * Preserve deep linked single result without re-sorting or pagination
     * Prevents the deep linked read from being lost when provider states update
     * or when other background updates trigger _updateResults()
     */
    if (
      this.sourceAndDeviceManagementEnabled &&
      this.isSdmDeepLinkActive &&
      this.results.length === 1 &&
      !this.isReviewingPlates
    ) {
      this._updateVisualization();
      return;
    }

    newResults = this._sortResultsByTimestamp(newResults);

    this.results = this._paginateResults(newResults);
    this._updateTotalResultsCount();
    this._updateVisualization();
    // Update export button state when results change
    this._updateExportButtonState();
  }

  get isNextPageDisabled(): boolean {
    if (this.isReviewingPlates) {
      return this.page * this.pageLimit >= this.reviewResults.length;
    }
    if (this.sourceAndDeviceManagementEnabled) {
      const results = this._collectProviderResults();
      return this.page * this.pageLimit >= results.length;
    } else {
      if (this.activeProvider) {
        return this.page * this.pageLimit >= this.activeProvider.results.length;
      }
      const results = this._collectProviderResults();

      return this.page * this.pageLimit >= results.length;
    }
  }

  /**
   * This method is used to initialize the plate review service and set up the appbar actions.
   * It fetches the reviewed plates and sets up the action to mark plates for review.
   */
  private _initPlateReview(): void {
    this._plateReviewService
      .getReviewedPlates()
      .pipe(takeUntil(this._destroy$))
      .subscribe((plates) => {
        this.reviewResults = plates;
        this._layoutService.appbar.actions.set([
          {
            icon: {
              template: `Marked for Review <span class="plate-count">${plates.length}</span>`,
            },
            class: 'mark-for-review-action',
            label: '',
            disabled: plates.length === 0,
            click: () => {
              this.page = 1;
              this.results = plates;
              this._clearErrors();
              this.isReviewingPlates = true;
              this._cd.markForCheck();
            },
          },
        ]);
      });
  }

  /**
   * Checks URL query parameters for point and distance parameters that represent a bounding box
   * to filter the search.
   *
   * This can happen when a user draws a shape on the main dashboard map and kicks off an LPR
   * search from there.
   */
  private _checkBoundingBoxParams(): void {
    const { point, dist, polygon } = this._activatedRoute.snapshot.queryParams;
    const hasCircleParams = point != null && dist != null;
    const hasPolygonParams = polygon != null;
    this.hasBoundingBoxParams = hasCircleParams || hasPolygonParams;
  }

  /**
   * Checks if any provider has warnings about unsupported filters
   * @returns true if any provider has warnings, false otherwise
   */
  public hasProviderWarnings(): boolean {
    if (!this.providers || this.providers.length === 0) {
      return false;
    }

    return this.providers.some((provider) => provider.warnings && provider.warnings.length > 0);
  }

  /**
   * Get the total number of problems (errors + warnings) for a provider
   * @param provider The provider to check
   * @returns The total count of errors and warnings
   */
  public getTotalProblems(provider: IFsLprProviderInterface): number {
    const errorCount = provider.errors?.length || 0;
    const warningCount = provider.warnings?.length || 0;
    return errorCount + warningCount;
  }

  /**
   * Checks if all providers have empty results
   * @returns true if all providers have zero results, false otherwise
   */
  public areAllProvidersEmpty(): boolean {
    return this.providers.every((provider) => provider.results.length === 0);
  }

  /**
   * Gets user-friendly names of filter limitations from provider warnings
   * @param provider The provider to check, or null to get combined limitations from all providers
   * @returns A formatted string listing the filter limitations
   */
  public getFilterLimitationsText(provider?: IFsLprProviderInterface | null): string {
    // If no provider is specified, combine all provider warnings
    const warningsToCheck = provider
      ? provider.warnings || []
      : this.providers.flatMap((p) => p.warnings || []);

    if (!warningsToCheck.length) return '';

    // Extract the filter names from the warnings
    const filterSet = new Set<string>();

    warningsToCheck.forEach((warning) => {
      filterSet.add(ERROR_TO_FILTER_NAME[warning.type]);
    });

    const filters = Array.from(filterSet);
    if (filters.length === 0) return '';

    return t('alpr.warnings.filterLimitations', { filters: filters.join(', ') });
  }

  /**
   * Gets warning tooltip for a plate - returns null if no warnings
   * @param plate The plate to check
   * @returns Tooltip string describing filter limitations, or null if no warnings
   */
  public getPlateWarningInfo(plate: FsLprFleetRead): string | null {
    // Find the provider that produced this plate
    const provider = this.providers.find((p) => {
      return p.results.some((result) => result.id === plate.id);
    });

    if (!provider || !provider.warnings || provider.warnings.length === 0) {
      return null;
    }

    return this.getFilterLimitationsText(provider) || t('alpr.warnings.providerHasLimitations');
  }

  /**
   * Calculates the optimal drawer width to fit the maximum number of result cards
   * while maintaining proper spacing
   * @param columns Number of columns to fit (defaults to 1)
   * @returns The optimal width in pixels
   */
  public calculateOptimalDrawerWidth(columns: number = 1): number {
    const { cardMinWidth, cardGap, containerPadding } = AlprReadsSearchComponent.DRAWER_CONFIG;
    return cardMinWidth * columns + cardGap * (columns - 1) + containerPadding;
  }

  /**
   * Determines how many columns of result cards could fit in the given width
   * @param width Width to check
   * @returns Number of columns that can fit, returns at least 1
   */
  public getColumnCountForWidth(width: number): number {
    const availableWidth = width - AlprReadsSearchComponent.DRAWER_CONFIG.containerPadding;
    const columns = Math.floor(
      (availableWidth + AlprReadsSearchComponent.DRAWER_CONFIG.cardGap) /
        (AlprReadsSearchComponent.DRAWER_CONFIG.cardMinWidth +
          AlprReadsSearchComponent.DRAWER_CONFIG.cardGap),
    );

    return Math.max(1, columns);
  }
  /**
   * Opens the CSV export dialog with current search results data
   */
  public openCsvExportDialog(): void {
    const dialogData: AlprExportDialogData = {
      providers: this.providers,
      totalResults: this.totalResultsCount,
      activeProvider: this.activeProvider,
    };

    const dialogRef = this._matDialog.open(CsvExportDialogComponent, {
      data: dialogData,
      width: '400px',
      maxWidth: '90vw',
      panelClass: 'csv-export-dialog-panel',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((result: AlprCsvExportConfiguration) => {
        if (result) {
          this.handleCsvExport();
        }
      });
  }

  /**
   * Updates the export button enabled state
   */
  private _updateExportButtonState(): void {
    const allProvidersDone = !this.providers.find((provider) => provider.isLoading);
    this.exportButtonEnabled =
      allProvidersDone && this.results.length > 0 && this.activeProvider === null;
  }

  /**
   * Handles the CSV export
   */
  private handleCsvExport(): void {
    // Create search parameters similar to the search method
    const searchParams: ILprExportParamsInterface = {
      ...this.formModel,
      inBbox:
        Object.keys(this._platesService.queriesSearchParams).length > 0
          ? this._platesService.queriesSearchParams
          : null,
    };

    // Format the export request data
    const exportRequest = this._requestFormatter.formatExportRequest(searchParams);

    // Track Mixpanel event for ALPR CSV export creation
    this._mixpanelService.trackMetric(MxEventName.CreateLprReportAf, {
      fileType: exportRequest.reportFormat,
    });

    // Create the export report - service handles all UI feedback
    this._lprExportService
      .createLprReport(exportRequest)
      .pipe(takeUntil(this._destroy$))
      .subscribe();
  }

  get hits(): IHitsInterface[] {
    return (this.selectedPlate as any)?.hits || [];
  }

  hasHits(): boolean {
    return this.hits && this.hits.length > 0;
  }

  hasVehicleDetails(): boolean {
    return !!(
      this.getVehicleColor() ||
      this.getVehicleMake() ||
      this.getVehicleModel() ||
      this.getVehicleYear() ||
      this.getVehicleBody()
    );
  }

  getVehicleColor(): string | null {
    return (this.selectedPlate as any)?.vehicleColor || null;
  }

  getVehicleMake(): string | null {
    return (this.selectedPlate as any)?.vehicleMake || null;
  }

  getVehicleModel(): string | null {
    return (this.selectedPlate as any)?.vehicleModel || null;
  }

  getVehicleYear(): string | null {
    return (this.selectedPlate as any)?.vehicleYear || null;
  }

  getVehicleBody(): string | null {
    return (
      (this.selectedPlate as any)?.vehicleBodyType ||
      (this.selectedPlate as any)?.vehicleBody ||
      null
    );
  }

  getPlateState(): string | null {
    return (
      (this.selectedPlate as any)?.plateState ||
      (this.selectedPlate as any)?.state ||
      (this.selectedPlate as any)?.displayState ||
      null
    );
  }

  getHotlistTitle(hit: IHitsInterface): string {
    if (!hit?.source) {
      return '—';
    }
    return hit.source
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
}
